import { BaseModal } from '@/components/base-modal';
import {
  useSimulationWatchers,
  useWatcherCandidates,
} from '@/pages/price-planning/simulation/simulation-queries';
import CopySimulationLinkButton from '@/pages/price-planning/simulation/simulation-tool-page/components/modals/copy-simulation-link-button';
import { useSimulationQueryErrorNotification } from '@/pages/price-planning/simulation/use-simulation-query-error-notification';
import {
  Combobox,
  HelpText,
  Label,
  Tag,
  Tooltip,
} from '@lego/connect-components-react';
import { useRef, useState } from 'react';

type AddRemoveWatchersModalProps = {
  isOpen: boolean;
  simulationId: number;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onConfirm: (userIds: string[]) => void;
  isSending: boolean;
  assigneeId: string;
};

const AddRemoveWatchersModal = ({
  isOpen,
  simulationId,
  triggerRef,
  onClose,
  onConfirm,
  isSending,
  assigneeId,
}: AddRemoveWatchersModalProps) => {
  const [search, setSearch] = useState('');
  const {
    data: candidates,
    isFetching,
    isError: isCandidatesError,
    error: candidatesError,
  } = useWatcherCandidates(simulationId, search, isOpen);
  useSimulationQueryErrorNotification({
    isError: isOpen && isCandidatesError,
    error: candidatesError,
    title: 'search watcher candidates',
  });

  const [selected, setSelected] = useState<
    { id: string; name: string }[] | undefined
  >();
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const {
    data: watchers,
    isLoading,
    isError,
    error,
  } = useSimulationWatchers(simulationId, isOpen);
  useSimulationQueryErrorNotification({
    isError: isOpen && isError,
    error,
    title: 'load watchers',
  });
  const selectedWatchers =
    selected ??
    watchers?.map((watcher) => ({
      id: watcher.userId,
      name: watcher.fullName,
    })) ??
    [];
  const selectedUserIds = selectedWatchers.map((watcher) => watcher.id);
  const options = [
    ...selectedWatchers,
    ...(candidates ?? []).filter(
      (candidate) => !selectedUserIds.includes(candidate.id),
    ),
  ];

  const hasChanges =
    selectedUserIds.length !== (watchers?.length ?? 0) ||
    selectedUserIds.some(
      (userId) => !watchers?.some((watcher) => watcher.userId === userId),
    );

  const removeSelected = (contactId: string) => {
    setSelected(selectedWatchers.filter((watcher) => watcher.id !== contactId));
  };

  const noEligibleUsers = !isFetching && candidates?.length === 0;

  const handleClose = () => {
    setIsComboboxOpen(false);
    setSearch('');
    setSelected(undefined);
    onClose();
  };

  const handleApply = () => {
    setIsComboboxOpen(false);
    onConfirm(selectedUserIds);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      modalRef={modalRef}
      triggerRef={triggerRef}
      onClose={handleClose}
      title="Add/Remove Watchers"
      size="small"
      rightButtonProps={{
        text: 'Apply',
        'aria-label': 'Apply',
        disabled: !hasChanges || isSending || isLoading || !watchers,
        loading: isSending,
        onClick: handleApply,
      }}
    >
      <CopySimulationLinkButton />
      <div>
        <div>
          <div>
            <Label forId="add-watchers-combobox">Add Watchers</Label>
            <div style={{ marginTop: '4px' }}>
              <Combobox
                id="add-watchers-combobox"
                multiple
                fullWidth
                value={selectedUserIds}
                portalTarget={modalRef.current}
                isOpen={isComboboxOpen}
                onOpenChange={setIsComboboxOpen}
                loading={isLoading || isFetching}
                placeholder="Type to search for a user"
                size="medium"
                onSearch={setSearch}
                filterFunction={(item, input) =>
                  selectedUserIds.includes(item.value) ||
                  item.label.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(_event, items) => {
                  const userIds = new Set(items.map((item) => item.value));
                  if (selectedUserIds.includes(assigneeId)) {
                    userIds.add(assigneeId);
                  }
                  setSelected(
                    options.filter((option) => userIds.has(option.id)),
                  );
                }}
                disabled={isLoading || isSending || !watchers}
              >
                {options.map((candidate) => (
                  <Combobox.Option key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </Combobox.Option>
                ))}
              </Combobox>
            </div>
            {noEligibleUsers && (
              <div style={{ marginTop: '4px' }}>
                <HelpText variant="neutral">No eligible users found.</HelpText>
              </div>
            )}
          </div>
          {selectedWatchers.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                marginTop: '16px',
              }}
            >
              {selectedWatchers.map((watcher) =>
                watcher.id === assigneeId ? (
                  <Tooltip key={watcher.id}>
                    <Tooltip.Trigger>
                      <span>
                        <Tag
                          variant="primary"
                          iconName="user-solid"
                          isOpen
                          disabled
                          aria-label={watcher.name}
                        >
                          {watcher.name}
                        </Tag>
                      </span>
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                      The current assignee can't be removed. Reassign the
                      simulation first.
                    </Tooltip.Content>
                  </Tooltip>
                ) : (
                  <Tag
                    key={watcher.id}
                    variant="primary"
                    iconName="user-solid"
                    isRemovable
                    isOpen
                    aria-label={watcher.name}
                    closeButtonLabel={`Remove ${watcher.name}`}
                    onDismiss={() => removeSelected(watcher.id)}
                  >
                    {watcher.name}
                  </Tag>
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </BaseModal>
  );
};
export default AddRemoveWatchersModal;
